import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, RefreshCw, Search, Smartphone, TrendingUp } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { getVersionStatistics } from '../api/AppOtaApi';
import { Toast } from '../components/ui';
import styles from './AppVersionStatistics.module.scss';

function AppVersionStatistics() {
  const { packageName } = useParams();
  const [versionStats, setVersionStats] = useState({
    total_devices: 0,
    version_distribution: [],
    device_details: [],
  });
  const [query, setQuery] = useState('');
  const [versionFilter, setVersionFilter] = useState('all');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const devicePanelRef = useRef(null);

  const fetchVersionStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVersionStatistics(packageName);
      setVersionStats(
        response.data || {
          total_devices: 0,
          version_distribution: [],
          device_details: [],
        }
      );
      setLastRefreshed(new Date());
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(
        `Could not load version adoption: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  }, [packageName]);

  useEffect(() => {
    fetchVersionStatistics();
    const interval = setInterval(fetchVersionStatistics, 30000);
    return () => clearInterval(interval);
  }, [fetchVersionStatistics]);

  const distribution = useMemo(
    () =>
      [...(versionStats.version_distribution || [])].sort(
        (a, b) => Number(b.version) - Number(a.version)
      ),
    [versionStats.version_distribution]
  );
  const latestVersion = distribution[0]?.version;
  const latestCount = distribution[0]?.device_count || 0;
  const adoption = versionStats.total_devices
    ? Math.round((latestCount / versionStats.total_devices) * 100)
    : 0;

  const filteredDevices = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (versionStats.device_details || []).filter(device => {
      const matchesVersion =
        versionFilter === 'all' || String(device.current_version) === String(versionFilter);
      const matchesQuery =
        !normalizedQuery ||
        [device.device_id, device.nickname, device.hub_serial_number, device.machine_nickname]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(normalizedQuery));
      return matchesVersion && matchesQuery;
    });
  }, [query, versionFilter, versionStats.device_details]);

  const formatDate = dateString =>
    dateString ? new Date(dateString).toLocaleString() : 'No check-in timestamp';

  // The device table may sit below the version list (stacked layout) or have
  // its header scrolled away, so bring it into view when a filter is applied.
  const toggleVersionFilter = version => {
    const next = versionFilter === version ? 'all' : version;
    setVersionFilter(next);
    const panel = devicePanelRef.current;
    if (next === 'all' || !panel) {
      return;
    }
    const { top } = panel.getBoundingClientRect();
    if (top < 80 || top > window.innerHeight - 160) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Rollout visibility · {packageName}</span>
          <h1>Version adoption</h1>
          <p>
            Track rollout progress and identify devices that have not reached the latest version.
          </p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={fetchVersionStatistics}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
          {loading ? 'Refreshing' : 'Refresh data'}
        </button>
      </header>

      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      <section className={styles.metrics} aria-label="Version adoption summary">
        <article>
          <span>
            <Smartphone size={17} /> Reporting devices
          </span>
          <strong>{versionStats.total_devices}</strong>
          <small>
            {lastRefreshed ? `Updated ${lastRefreshed.toLocaleTimeString()}` : 'Waiting for data'}
          </small>
        </article>
        <article>
          <span>
            <CheckCircle2 size={17} /> Latest version
          </span>
          <strong>{latestVersion !== undefined ? `v${latestVersion}` : '—'}</strong>
          <small>
            {distribution.length} version{distribution.length === 1 ? '' : 's'} currently active
          </small>
        </article>
        <article className={styles.adoptionMetric}>
          <span>
            <TrendingUp size={17} /> Latest-version adoption
          </span>
          <strong>{versionStats.total_devices ? `${adoption}%` : '—'}</strong>
          <progress max="100" value={adoption}>
            {adoption}%
          </progress>
          <small>
            {latestCount} of {versionStats.total_devices} reporting devices
          </small>
        </article>
      </section>

      <div className={styles.contentGrid}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <span>Rollout progress</span>
              <h2>Version distribution</h2>
            </div>
          </div>
          {distribution.length ? (
            <div className={styles.distributionList}>
              {distribution.map((version, index) => {
                const percentage = versionStats.total_devices
                  ? Math.round((version.device_count / versionStats.total_devices) * 100)
                  : 0;
                return (
                  <button
                    type="button"
                    key={version.version}
                    className={
                      versionFilter === String(version.version) ? styles.distributionActive : ''
                    }
                    onClick={() => toggleVersionFilter(String(version.version))}
                  >
                    <div>
                      <strong>v{version.version}</strong>
                      {index === 0 && <span>Latest</span>}
                      <b>{percentage}%</b>
                    </div>
                    <progress max="100" value={percentage}>
                      {percentage}%
                    </progress>
                    <small>
                      {version.device_count} device{version.device_count === 1 ? '' : 's'}
                    </small>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <TrendingUp size={25} />
              <strong>No check-ins yet</strong>
              <p>Version data appears after devices contact the update service.</p>
            </div>
          )}
        </section>

        <section className={`${styles.panel} ${styles.devicePanel}`} ref={devicePanelRef}>
          <div className={styles.deviceToolbar}>
            <div>
              <span>Device detail</span>
              <h2>
                {versionFilter === 'all' ? 'All reporting devices' : `Devices on v${versionFilter}`}
              </h2>
            </div>
            <div className={styles.filters}>
              {versionFilter !== 'all' && (
                <button type="button" onClick={() => setVersionFilter('all')}>
                  Clear version filter
                </button>
              )}
              <label className={styles.searchField}>
                <Search size={15} />
                <span className={styles.srOnly}>Search devices</span>
                <input
                  type="search"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search device or machine"
                />
              </label>
            </div>
          </div>
          {filteredDevices.length ? (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Hub / machine</th>
                    <th>Version</th>
                    <th>Last check-in</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map(device => {
                    const isLatest = String(device.current_version) === String(latestVersion);
                    return (
                      <tr key={device.device_id}>
                        <td data-label="Device">
                          <div className={styles.deviceIdentity}>
                            <strong>{device.nickname || 'Unnamed device'}</strong>
                            <code>{device.device_id}</code>
                          </div>
                        </td>
                        <td data-label="Hub / machine">
                          <div className={styles.hubIdentity}>
                            <strong>{device.machine_nickname || 'Unlinked machine'}</strong>
                            <code>{device.hub_serial_number || 'No hub serial'}</code>
                          </div>
                        </td>
                        <td data-label="Version">
                          <span
                            className={`${styles.versionBadge} ${isLatest ? styles.latestBadge : styles.outdatedBadge}`}
                          >
                            v{device.current_version}
                            {isLatest ? ' · latest' : ''}
                          </span>
                        </td>
                        <td data-label="Last check-in">{formatDate(device.last_updated)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Smartphone size={25} />
              <strong>No matching devices</strong>
              <p>Clear the filters or try a different search.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AppVersionStatistics;
